import { useState, useEffect, useCallback } from 'react';
import { apiService, ProgressMessage } from '../services/api';

const API_BASE_URL = 'https://oyster-app-9l6qa.ondigitalocean.app/http://51.20.109.105:8000';
// const API_BASE_URL = 'http://localhost:8000';

export interface UseVideoProcessingReturn {
  isProcessing: boolean;
  progress: number;
  progressText: string;
  currentStep: string;
  error: string | null;
  taskId: string | null;
  isUploading: boolean; // Флаг загрузки файла
  uploadProgress: number; // Процент загрузки (0-100)
  startProcessing: (request: {
    video: File;
    email: string;
    duration: number;
    cost: number;
    source_language: string;
    target_language: string;
    speed?: number;
    add_subtitles?: boolean;
  }) => Promise<void>;
  stopProcessing: () => void;
}

export const useVideoProcessing = (): UseVideoProcessingReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [currentStep, setCurrentStep] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const startProcessing = useCallback(async (request: {
    video: File;
    email: string;
    duration: number;
    cost: number;
    source_language: string;
    target_language: string;
    speed?: number;
    add_subtitles?: boolean;
  }) => {
    try {
      setError(null);
      setIsProcessing(true);
      setIsUploading(true);
      setProgress(0);
      setUploadProgress(0);
      setProgressText('Initializing upload...');
      setCurrentStep('initializing');

      // Step 1: Initialize upload (instant response)
      if (import.meta.env.DEV) {
        console.log('Step 1: Initializing upload...');
      }
      const initResponse = await apiService.initUpload({
        email: request.email,
        duration: request.duration,
        cost: request.cost,
        source_language: request.source_language,
        target_language: request.target_language,
        filename: request.video.name,
        file_size: request.video.size,
        speed: request.speed,
        add_subtitles: request.add_subtitles,
      });

      if (import.meta.env.DEV) {
        console.log('Upload initialized:', initResponse);
      }
      setTaskId(initResponse.task_id);
      setProgressText('Starting upload...');
      setCurrentStep('uploading');

      // Start polling immediately
      if (import.meta.env.DEV) {
        console.log('Starting progress polling for task:', initResponse.task_id);
      }
      startProgressPolling(initResponse.task_id);

      // Step 2: Upload video file with client-side progress tracking
      if (import.meta.env.DEV) {
        console.log('Step 2: Uploading video file...');
      }
      await apiService.uploadVideo(
        initResponse.task_id,
        request.video,
        (percent) => {
          // Client-side upload progress
          setUploadProgress(percent);
          setProgress(percent);
          setProgressText(`Uploading video... ${percent}%`);
        }
      );

      if (import.meta.env.DEV) {
        console.log('Upload complete, processing started');
      }
      setIsUploading(false);
      setProgressText('Upload complete, processing...');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setIsProcessing(false);
      setIsUploading(false);
      setProgress(0);
      setUploadProgress(0);
      setProgressText('');
      setCurrentStep('');
    }
  }, []);

  const startProgressPolling = useCallback((taskId: string) => {
    const progressInterval = setInterval(async () => {
      try {
        const status = await apiService.getTaskStatus(taskId);
        console.log('Status check result:', status);
        
        // Calculate progress percentage based on progress message
        const progressPercentageMap: { [key: string]: number } = {
          'Задача создана': 5,
          'Извлечение аудио из видео...': 15,
          'Транскрипция аудио...': 30,
          'Перевод на': 50,
          'Клонирование голоса...': 60,
          'Генерация речи с клонированным голосом...': 75,
          'Наложение аудио на видео...': 90,
          'Очистка...': 95,
          'Готово!': 100
        };
        
        // Update progress based on status
        if (status.status === 'uploading') {
          // Показываем прогресс загрузки файла (серверный, если есть)
          // Но не перезаписываем клиентский прогресс, если он больше
          if (status.upload_progress !== undefined) {
            setIsUploading(true);
            // Используем максимум между клиентским и серверным прогрессом
            setUploadProgress(prev => Math.max(prev, status.upload_progress || 0));
            setProgress(prev => Math.max(prev, status.upload_progress || 0));
            setProgressText(status.progress || 'Uploading video...');
            setCurrentStep('uploading');
          }
        } else if (status.status === 'pending') {
          // Файл загружен, переходим к обработке
          setIsUploading(false);
          setUploadProgress(100);
          setProgress(5);
          setProgressText(status.progress || 'Task created');
          setCurrentStep('pending');
        } else if (status.status === 'processing') {
          // Обработка видео
          setIsUploading(false);
          // Try to get progress percentage from message
          let progressPercent = 50; // default
          for (const [key, value] of Object.entries(progressPercentageMap)) {
            if (status.progress.includes(key)) {
              progressPercent = value;
              break;
            }
          }
          
          setProgress(progressPercent);
          setProgressText(status.progress || 'Processing...');
          setCurrentStep('processing');
        } else if (status.status === 'completed') {
          setProgress(100);
          setProgressText('Processing completed!');
          setCurrentStep('complete');
          setIsProcessing(false);
          clearInterval(progressInterval);
          
          // Store download URL for video player
          if (status.result_url) {
            // Check if result_url is already a full URL
            if (status.result_url.startsWith('http')) {
              (window as any).processedVideoUrl = status.result_url;
            } else {
              (window as any).processedVideoUrl = `${API_BASE_URL}${status.result_url}`;
            }
          } else {
            // Fallback: construct download URL
            (window as any).processedVideoUrl = `${API_BASE_URL}/download/${taskId}`;
          }
        } else if (status.status === 'failed') {
          setError(status.error || 'Processing failed');
          setIsProcessing(false);
          clearInterval(progressInterval);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('Status check failed:', err);
        }
        // Don't stop polling on network errors, just log them
      }
    }, 1000); // Check every 1 second for faster updates

    // Store interval ID for cleanup
    (window as any).progressInterval = progressInterval;
  }, []);

  const stopProcessing = useCallback(() => {
    // Clear polling interval
    if ((window as any).progressInterval) {
      clearInterval((window as any).progressInterval);
      (window as any).progressInterval = null;
    }
    
    // Clear WebSocket if exists (for backward compatibility)
    if (ws) {
      ws.close();
      setWs(null);
    }
    
    setIsProcessing(false);
    setProgress(0);
    setProgressText('');
    setCurrentStep('');
    setError(null);
    setTaskId(null);
    setIsUploading(false);
    setUploadProgress(0);
  }, [ws]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear polling interval
      if ((window as any).progressInterval) {
        clearInterval((window as any).progressInterval);
      }
      
      // Clear WebSocket if exists
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  return {
    isProcessing,
    progress,
    progressText,
    currentStep,
    error,
    taskId,
    isUploading,
    uploadProgress,
    startProcessing,
    stopProcessing,
  };
};
